<template>
    <div class="container mt-3">
      <p v-if="expectedVerificationCode">{{ expectedVerificationCode }}</p>
      <input type="hidden" name="code" :value="verificationCode" />
      <div class="header">
        <div class="phone-icon-container">
          <div class="phone-icon">
            <phone-icon size="38" />
          </div>
        </div>
        <div >
          <p class="phone-number-verification-title text-center"> {{ i18nStrings.title }} </p>
          <p class="font-semibold phone-number-verification-description text-center px-2">
              {{ i18nStrings.description }}
          </p>
          <p class="phone-number-verification-timer text-center"  >
             {{ formattedCountdown }} 
          </p>
      </div>
      <ui-alert
        type="warning"
        :title="errorTitle"
        :description="errorMessage"
        v-if="isError"
        class="my-4">
      </ui-alert>
      <input-digits
          :length="6"
          @input="onInputCode"
          :value="verificationCode"
        />
        <div class="resend-code-form">
          <p class="text-center">
            {{ i18nStrings.didntReceiveText }} 
            <ui-button @click="resendCode" appearance="primary" button-type="blank" class="p-0" :disabled="isCooldownActive" :label="i18nStrings.resendText"/>
          </p>
        </div>
      </div>
      <div class="verification-footer">
          <ui-button appearance="secondary" uppercase block @click="submitCode" :disabled="isConfirmButtonDisabled" :label="i18nStrings.confirmCodeButton"/>
          <ui-button
            appearance="secondary"
            :href="changePhoneNumberUrl"
            button-type="blank"
            uppercase
            block
            :label="i18nStrings.changeNumberButton"
          />
      </div>
    </div>
</template>
  
<script>
// import Translations from "./insert-me-to-translate.vue";
import {getCookie} from "../../../../backoffice/static/js/services/cookies";

export default{
    components:{
      UiButton: () => import(/* webpackChunkName: "chunk-ui-alert" */ './ui/ui-button.vue'),
      PhoneIcon:() => import(/* webpackChunkName: "chunk-phone-icon" */ './svg/phone-icon.vue'),
      InputDigits: () => import(/* webpackChunkName: "chunk-input-digits" */ './input/input-digits.vue'),
      UiAlert: () => import(/* webpackChunkName: "chunk-ui-alert" */ './ui/ui-alert.vue'),
    },
    props:{
      phoneNumber: { type: String, required: true },
      codeCheckUrl: { type: String, required: true },
      codeResendUrl: { type: String, required: true },
      changePhoneNumberUrl: { type: String, required: true },
      csrfToken: { type: String, required: true },
      successUrl: { type: String, required: true },
      expectedVerificationCode: { type: String, required: false },
    },

    data(){
      return {
        isCooldownActive: true, 
        countdownTimer: null,
        countdown: 90, // 90 seconds countdown
        verificationCode: "",
        isError: false,
        errorMessage:"",
        errorTitle:"",
        i18nStrings: {
            title: gettext("Check your phone"),
            description:interpolate( gettext("We’ve sent a 6-digit code to %s"), [this.phoneNumber]),
            confirmCodeButton: gettext("Confirm code"),
            changeNumberButton: gettext("Change number"),
            didntReceiveText: gettext("Didn’t receive the SMS?"),
            resendText: gettext("Click to resend"),
            genericErrorTitle: gettext("Oops, something went wrong."),
            genericErrorMessage: gettext("Log in or try again in a few minutes."),
        }
      }
    },
    mounted() {
      // Start the 30-second disable timer
      setTimeout(() => {
        this.isCooldownActive = false;
      }, 30000);
    
      // Start the 90-second countdown
      this.countdownTimer = setInterval(() => {
        if (this.countdown > 0) {
          this.countdown--;
        } else {
          clearInterval(this.countdownTimer); // stop when countdown reaches 0
        }
      }, 1000);
    },
    beforeDestroy() {
        // Clean up interval
        if (this.countdownTimer) {
          clearInterval(this.countdownTimer);
        }
    },

    computed: {
      isConfirmButtonDisabled() {
        return this.verificationCode.length !== 6;
      },
      formattedCountdown() {
        const minutes = Math.floor(this.countdown / 60);
        const seconds = this.countdown % 60;
        return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
      }
    },

    methods:{
      onInputCode(value) {
        this.verificationCode = value;
      },
      async resendCode() {
        try {
          const response = await fetch(this.codeResendUrl, {
            method: "POST",
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': getCookie('csrftoken'),
              'X-Requested-With': 'XMLHttpRequest',
            },
          });
        
          if (response.ok) {
            const data =  await response.json();
            if(data.status === 'success'){
              this.countdown = 90; // reset countdown
              this.isCooldownActive = true;
              setTimeout(() => {
                this.isCooldownActive = false;
              }, 30000);
            }else{
              console.error('Failed to resend code', response.status);
            }
          } else {
            console.error('Failed to resend code', response.status);
          }
        } catch (error) {
          console.error('An error occurred:', error);
        }
      },
      setGenericError(){
        this.isError= true;
        this.errorTitle = this.i18nStrings.genericErrorTitle
        this.errorMessage = this.i18nStrings.genericErrorMessage
      },
      async submitCode(){
        if(this.verificationCode.length == 6) {
          try {
            let body = {
              'code': this.verificationCode
            };
            const response = await fetch(this.codeCheckUrl, {
              method: "POST",
              headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': getCookie('csrftoken'),
                'X-Requested-With': 'XMLHttpRequest',
              },
              body: JSON.stringify(body)
            });
            if (response.ok) {
              const data = await response.json();
              if(data.status ===  'success'){
                this.$skokka.navigateTo(this.successUrl);
              }else{
                if(data.data?.too_many_requests){
                  this.isError= true;
                  this.errorTitle = gettext("You've made too many requests")
                  this.errorMessage = gettext("For security reasons, please try again in one hour.")
                } else if(data.data?.incorrect_code){ 
                  this.isError= true;
                  this.errorTitle = gettext("Incorrect code")
                  this.errorMessage = gettext("Try again or request a new one.")
                }else{
                  this.setGenericError();
                }
              }
            } else {
              this.setGenericError();
            }
          } catch (error) {
            console.error('An error occurred:', error);
            this.setGenericError();
          }
        }
      }
    }
}
</script>
<style>
  .phone-icon{
    display: flex;
    width: 76px;
    height: 76px;
    padding: 10px;
    justify-content: center;
    align-items: center;
    border-radius: 38px;
    border: 13.571px solid #FFF8FC;
    background: #FFEAF4;
  }

  .phone-icon-container{
    display: flex;
    justify-content: center;
    align-items: center;
  }
  
  .resend-code-form{
    display: flex;
    justify-content: center;
    align-items: flex-start;
    gap: 1px;
    align-self: stretch;
  }

  .header{
    padding: 20px 15px;
  }

  .verification-footer{
    padding: 0 15px;
    margin-top: 30px;
  }

 
</style>